import { Component, OnDestroy, OnInit } from '@angular/core';
import { finalize, forkJoin, Subscription, switchMap } from 'rxjs';
import moment from 'moment';
import { ProductService } from '../../products/product.service';
import { ProductNameList } from '../../products/product.model';
import { buildFilterQuery, convertUTCtoIST } from '../../utils';
import { ProductionInventoryService } from '../../production-inventory/services/production-inventory.service';
import { ProductionEntryInsight } from '../../production-inventory/production-inventory.model';
import { RawMaterialService } from '../../raw-material-inventory/raw-material.service';
import { RawMaterialWarehouse } from '../../raw-material-inventory/raw-material.model';
import { User } from '../../authentication/auth.models';
import { SettingsService } from '../../settings/settings.service';
import { UserRole } from '../../employees/employees.model';
import { DesignationEnum } from '../../common/models/common.model';
import { AttendanceService } from '../../attendance/attendance.service';
import { EmployeeAttendance, PresenceStatus } from '../../attendance/attendance.model';
import { getDefaultProfilePicUrl, handleImageError } from '../../utils';
import { DashboardService } from '../dashboard.service';
import { DashboardSummary } from '../dashboard.model';

@Component({
  selector: 'app-dashboard',
  standalone: false,
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, OnDestroy {
  productList: ProductNameList[] = [];
  yearsList: number[] = [];
  productionEntries: ProductionEntryInsight[] = [];
  warehouseList: RawMaterialWarehouse[] = [];
  isDashboardDataLoading: boolean = true;
  currentUser: User | null = null;

  selectedProduct: string | null = null;
  selectedWarehouse: string | null = null;
  selectedYear: number = moment().year();
  startDate: string = moment().subtract(1, 'month').format('YYYY-MM-DD');
  endDate: string = moment().format('YYYY-MM-DD');
  employeesTodayAttendance: EmployeeAttendance[] = [];
  pagedEmployees: EmployeeAttendance[] = [];
  attendanceCurrentPage: number = 1;
  attendancePageSize: number = 5;
  isAttendanceLoading: boolean = false;

  // ── Day-wise Snapshot ──────────────────────────────────────────────────────
  summary: DashboardSummary | null = null;
  isSnapshotLoading = false;
  snapshotDate: string = moment().format('YYYY-MM-DD');
  openSection: 'orders' | 'payments' | 'purchases' | 'pending' | null = 'orders';

  private _dashboardSubscription!: Subscription;

  constructor(
    private _productService: ProductService,
    private productionInventoryService: ProductionInventoryService,
    private _rawMaterialService: RawMaterialService,
    private settingsService: SettingsService,
    private _attendanceService: AttendanceService,
    private dashboardService: DashboardService
  ) {}

  ngOnInit(): void {
    this.settingsService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });

    this._getDashboardData();
  }

  // ── Snapshot ────────────────────────────────────────────────────────────────

  protected onDateChange(): void {
    this.isSnapshotLoading = true;
    this.dashboardService
      .getSummary(this.snapshotDate)
      .pipe(finalize(() => (this.isSnapshotLoading = false)))
      .subscribe({ next: data => (this.summary = data) });
  }

  protected toggleSection(section: 'orders' | 'payments' | 'purchases' | 'pending'): void {
    this.openSection = this.openSection === section ? null : section;
  }

  protected maxByCompanyBoxes(): number {
    if (!this.summary?.today_orders.by_company.length) return 1;
    return Math.max(...this.summary.today_orders.by_company.map(b => b.total_boxes));
  }

  protected maxByPaymentTotal(): number {
    if (!this.summary?.today_payments.by_payment_type.length) return 1;
    return Math.max(...this.summary.today_payments.by_payment_type.map(b => b.total));
  }

  // Protected methods

  protected applyFilters(): void {
    const reqParams = buildFilterQuery({
      product_id: this.selectedProduct,
      warehouse: this.selectedWarehouse,
      from_date: this.startDate,
      to_date: this.endDate,
      year: this.selectedYear,
    });

    this.isDashboardDataLoading = true;
    this.productionEntries = [];

    this._dashboardSubscription = this.productionInventoryService
      .insights(reqParams)
      .pipe(finalize(() => (this.isDashboardDataLoading = false)))
      .subscribe({
        next: (res: ProductionEntryInsight[]) => {
          this.productionEntries = res;
        },
      });
  }

  // Private methods

  private _getDashboardData(): void {
    this.isDashboardDataLoading = true;

    const productionYears$ = this.productionInventoryService.years();
    const productsList$ = this._productService.lighter_list();
    const warehousesList$ = this._rawMaterialService.get_warehouses();
    const todayAttendance$ = this._attendanceService.today_attendance({ status: PresenceStatus.CHECK_IN });
    const snapshot$ = this.dashboardService.getSummary(this.snapshotDate);

    this.isAttendanceLoading = true;

    this._dashboardSubscription = forkJoin([
      productionYears$,
      productsList$,
      warehousesList$,
      todayAttendance$,
      snapshot$,
    ])
      .pipe(
        switchMap(
          ([
            productionYearsResponse,
            productsListResponse,
            warehousesListResponse,
            attendanceResponse,
            snapshotResponse,
          ]) => {
            /* ---------------- Snapshot ---------------- */
            this.summary = snapshotResponse;
            /* ---------------- Attendance ---------------- */
            this.employeesTodayAttendance = attendanceResponse;
            this.attendanceCurrentPage = 1;
            this._updatePagedEmployees();
            this.isAttendanceLoading = false;

            /* ---------------- Years ---------------- */
            const productionYears = productionYearsResponse.years;

            if (productionYears.length === 0) {
              productionYears.push(moment().year());
            }

            this.yearsList = productionYears;
            this.selectedYear = this.yearsList[0];

            /* ---------------- Products ---------------- */
            this.productList = productsListResponse;

            /* ---------------- Warehouses ---------------- */
            this.warehouseList = warehousesListResponse;

            if (this.currentUser?.role !== UserRole.ADMIN) {
              const plantHeadWarehouse = this.warehouseList.find(w => w.plant_head?.user_id === this.currentUser?.id);

              if (plantHeadWarehouse) {
                this.selectedWarehouse = plantHeadWarehouse.id;
              }
            }

            /* ---------------- Build filters AFTER warehouse is known ---------------- */
            const reqParams = buildFilterQuery({
              year: this.selectedYear,
              start_date: this.startDate,
              end_date: this.endDate,
              warehouse: this.selectedWarehouse,
            });

            return this.productionInventoryService.insights(reqParams);
          }
        ),
        finalize(() => (this.isDashboardDataLoading = false))
      )
      .subscribe({
        next: productionEntriesResponse => {
          this.productionEntries = productionEntriesResponse;
        },
      });
  }

  ngOnDestroy(): void {
    this._dashboardSubscription?.unsubscribe();
  }

  // Attendance Pagination
  protected onAttendancePageChange(page: number): void {
    this.attendanceCurrentPage = page;
    this._updatePagedEmployees();
  }

  private _updatePagedEmployees(): void {
    const startIndex = (this.attendanceCurrentPage - 1) * this.attendancePageSize;
    const endIndex = startIndex + this.attendancePageSize;
    this.pagedEmployees = this.employeesTodayAttendance.slice(startIndex, endIndex);
  }

  protected readonly UserRole = UserRole;
  protected readonly DesignationEnum = DesignationEnum;
  protected readonly getDefaultProfilePicUrl = getDefaultProfilePicUrl;
  protected readonly handleImageError = handleImageError;
  protected readonly moment = moment;
  protected readonly Math = Math;
  protected readonly convertUTCtoIST = convertUTCtoIST;
}
